import React, {memo, useCallback, useEffect} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {notification, Popover} from "antd";
import NotificationActions from '../../redux/Notification/actions';
import IconButton from "./IconButton";
import NotificationModal from "./Modal";
import NotificationDropdown from "./Dropdown";
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import NotificationContent from './NotificationContent';
import NotificationTitle from './NotificationTitle';
import { NotificationType } from '../../enums/NotificationType';
import { useAsyncSideEffects } from './asyncSideEffectsHelper';

const {
    readNotification,
    onCreatedNotification,
    onReadNotification,
    setAsReadNotification,

    openDropdown,
    closeDropdown,

    openModal,
} = NotificationActions;

function TopbarNotification() {
    const sessionReady = useSelector(state => state.Auth.sessionReady);
    const websocket = useSelector(state => state.App.websocket, shallowEqual);
    const modalVisible = useSelector(state => state.Notification.modalVisible);
    const dropdownVisible = useSelector(state => state.Notification.dropdownVisible);
    const gridSize = useSelector(state => state.App.gridSize);

    const dispatch = useDispatch();

    notification.config({
        maxCount: 4,
        placement: 'bottomRight',
    });

    const {fetchDataAfterNotified, updateCompanyProfileRedux} = useAsyncSideEffects();

    useEffect(() => {
        if (sessionReady && websocket) {
            dispatch(readNotification());
            const channel = websocket.channel('notification', true);

            // Subscribe
            channel
                .listen('.notification.created', data => {
                    dispatch(onCreatedNotification(data));
                    const { notificationData } = data;
                    fetchDataAfterNotified(notificationData);

                    const {
                        content,
                        content_type,
                        popup_type, // 'SUCCESS', 'ERROR', 'WARNING', 'INFO', null
                    } = notificationData;

                    const antdPopupType = popup_type ? popup_type.toLowerCase() : 'open';

                    notification[antdPopupType]({
                        message: (
                            <NotificationTitle notificationData={notificationData} />
                        ),
                        description: (
                            <I18nProvider i18n={i18n}>
                                <NotificationContent notificationData={notificationData} />
                            </I18nProvider>
                        ),
                        duration: 5,
                        onClick: () => {
                            // eg after maybank bank feed disconnect successfully
                            if (content_type === NotificationType.text && content)
                                dispatch(openModal(notificationData, false));
                        },
                    });
                });

            channel
                .listen('.notification.read', () => {
                    dispatch(onReadNotification());
                });

            const eventChannel = websocket.channel('event');

            eventChannel
                .listen('.event.company_profile_updated', data => {
                    updateCompanyProfileRedux(data);
                });
        }
        return () => {
            websocket?.leaveChannel('notification', true);
            websocket?.leaveChannel('event');
        };
    }, [sessionReady, websocket, dispatch, fetchDataAfterNotified, updateCompanyProfileRedux]);

    const onVisibleChange = useCallback(() => {
        if (!dropdownVisible)
            dispatch(setAsReadNotification());
        if (!modalVisible)
            dispatch(dropdownVisible ? closeDropdown() : openDropdown());
    }, [dispatch, modalVisible, dropdownVisible]);

    return [
        <NotificationModal key="modal" />,
        <Popover
            key="button"
            content={<NotificationDropdown />}
            trigger="click"
            overlayClassName="notification-popover"
            open={dropdownVisible}
            onOpenChange={onVisibleChange}
            // arrowPointAtCenter
            arrow={{
                ...gridSize !== 'xs' && {arrowPointAtCenter: true},
            }}
            placement={gridSize === 'xs' ? 'bottom' : 'bottomLeft'}
        >
            <IconButton />
        </Popover>,
    ];
}

export default memo(TopbarNotification);
